import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { MaskedInput } from '@/shared/components/masked-input';
import { maskBirthday, maskPhone } from '@/shared/lib/input-masks';
import { onlyDigits } from '@/shared/lib/format';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { ApiError } from '@/shared/api/client';
import { detectClientPlatform } from '@/shared/lib/device';
import {
  buildInviteRegisterPayload,
  emptyInviteForm,
  GENDER_OPTIONS,
  inviteMarketingConsent,
  validateInviteForm,
  type InviteFormState,
} from '../model/invite-form-schema';
import { resolveInviteFieldPlan, isBubblemixSlug } from '../model/invite-variant';
import { defaultDialCode } from '../model/intl-phone';
import {
  registerInvite,
  validateUniqueIdAvailable,
  type InviteDepartment,
  type InviteReferral,
  type PublicInvitePassbook,
  type RegisterInviteResult,
} from '../api/use-public-invite';
import { PublicInviteTermsCheckbox } from './public-invite-terms-checkbox';
import { PublicInviteMarketingConsent } from './public-invite-marketing-consent';
import { PublicInviteIdentityField } from './public-invite-identity-field';
import { PublicInviteStateCityFields } from './public-invite-state-city-fields';
import { PublicInviteCustomFields } from './public-invite-custom-fields';
import { PublicInviteChildrenGroup } from './public-invite-children-group';
import { PublicInviteSubmittingOverlay } from './public-invite-submitting-overlay';
import { PublicInviteFormPanel } from './public-invite-form-panel';

interface PublicInviteWizardProps {
  passbook: PublicInvitePassbook;
  /** `?campanha=` da URL — repassado sem transformação, igual ao legado (qualquer loja). */
  campanha?: string;
  /** Multi-loja/departamento — presente só em `/invite/:id/:idOffice/:idDepartment`. */
  department?: InviteDepartment;
  /** Veio de um link "Indique e ganhe". */
  referral?: InviteReferral;
  onSuccess: (result: RegisterInviteResult) => void;
}

/** Identificador completo o bastante para registrar a indicação. */
function identityComplete(identity: string, value: string): boolean {
  const digits = onlyDigits(value);
  if (identity === 'cpf') return digits.length === 11;
  if (identity === 'cnpj') return digits.length === 14;
  if (identity === 'car-plate') return value.replace(/[^a-z0-9]/gi, '').length >= 7;
  return false;
}

export function PublicInviteWizard({
  passbook,
  campanha,
  department,
  referral,
  onSuccess,
}: PublicInviteWizardProps) {
  const plan = useMemo(() => resolveInviteFieldPlan(passbook), [passbook]);
  // A maioria dos passbooks tem addFields configurado no banco mas o template legado
  // nunca os renderiza no cadastro público — só variantes com showCustomFields mostram.
  const addFields = plan.showCustomFields ? (passbook.addFields ?? []) : [];

  const [form, setForm] = useState<InviteFormState>(() =>
    emptyInviteForm({
      uniqueId: plan.identity === 'hidden-generated' ? passbook.generatedUniqueId : '',
      countryCode: defaultDialCode(plan.locale),
    }),
  );
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const set = <K extends keyof InviteFormState>(key: K, value: InviteFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setCustomField = (slug: string, value: string) =>
    setForm((prev) => ({ ...prev, customFields: { ...prev.customFields, [slug]: value } }));

  const canValidateUniqueId =
    passbook.validationUniqueId && (plan.identity === 'cpf' || plan.identity === 'cnpj');

  const handleUniqueIdBlur = async () => {
    if (referral?.onIdentify && identityComplete(plan.identity, form.uniqueId)) {
      referral.onIdentify(form.uniqueId);
    }
    if (!canValidateUniqueId || !form.uniqueId) return;
    const ok = await validateUniqueIdAvailable(passbook.idPassbook, onlyDigits(form.uniqueId));
    if (!ok) {
      toast.error(`${passbook.fieldUniqueIdLabel} não cadastrado na plataforma!`);
      set('uniqueId', '');
    }
  };

  const handleFinish = async () => {
    const validation = validateInviteForm(form, passbook.fieldUniqueIdLabel, plan, addFields);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }
    setSubmitting(true);
    setSucceeded(false);
    try {
      const platform = detectClientPlatform();
      const payload = buildInviteRegisterPayload(form, plan, addFields.length > 0);
      const marketingConsent = inviteMarketingConsent(form, passbook.marketingChannels);
      const result = await registerInvite(
        passbook.idPassbook,
        {
          ...payload,
          platform,
          campanha,
          ...(referral ? { mgmCode: referral.code } : {}),
          ...(marketingConsent ? { marketingConsent } : {}),
        },
        department,
      );
      setSucceeded(true);
      // Deixa o "check" do morfismo aparecer antes de trocar pra tela de sucesso/QR.
      await new Promise((resolve) => setTimeout(resolve, 650));
      onSuccess(result);
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível concluir seu cadastro. Tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // brincarte: os campos abaixo são da criança, não de quem preenche — só relabela, mesma estrutura.
  const childSuffix = plan.childLabels ? ' da Criança' : '';

  const identityField = (
    <PublicInviteIdentityField
      identity={plan.identity}
      uniqueIdLabel={`${passbook.fieldUniqueIdLabel}${childSuffix}`}
      uniqueId={form.uniqueId}
      onUniqueIdChange={(v) => set('uniqueId', v)}
      onUniqueIdBlur={canValidateUniqueId || referral ? handleUniqueIdBlur : undefined}
      countryCode={form.countryCode}
      onCountryCodeChange={(v) => set('countryCode', v)}
      phone={form.phone}
      onPhoneChange={(v) => set('phone', v)}
    />
  );
  const nameField = (
    <div className="space-y-2">
      <Label htmlFor="invite-name">
        Nome completo{childSuffix} <span className="text-destructive">*</span>
      </Label>
      <Input
        id="invite-name"
        autoComplete="name"
        placeholder="Preencha seu nome"
        value={form.name}
        onChange={(e) => set('name', e.target.value)}
      />
    </div>
  );
  const emailField = (
    <div className="space-y-2">
      <Label htmlFor="invite-email">
        Email <span className="text-destructive">*</span>
      </Label>
      <Input
        id="invite-email"
        type="email"
        autoComplete="email"
        placeholder="Preencha seu e-mail"
        value={form.email}
        onChange={(e) => set('email', e.target.value)}
      />
    </div>
  );
  // Legado sempre mostra CPF/CNPJ/Placa como o PRIMEIRO campo do form, antes até do nome
  // (views/invite.ejs, invite_ciadamacaxeirab2b.ejs, invite_goflashwash.ejs) — intl-phone/
  // hidden-generated não têm esse "campo de documento" isolado, então mantêm nome primeiro.
  const identityFirst =
    plan.identity === 'cpf' || plan.identity === 'cnpj' || plan.identity === 'car-plate';
  // goflashwash: addFields (CPF do responsável) fica pareado com Nome, logo após a Placa —
  // sequência de grupos própria (invite_goflashwash.ejs), diferente de todo o resto.
  const isCarPlate = plan.identity === 'car-plate';

  const customFieldsBlock = (
    <PublicInviteCustomFields
      fields={addFields}
      values={form.customFields}
      onChange={setCustomField}
    />
  );

  const birthdayField = plan.showBirthday ? (
    <div className="space-y-2">
      <Label htmlFor="invite-birthday">
        Data de Nascimento{childSuffix} <span className="text-destructive">*</span>
      </Label>
      <MaskedInput
        id="invite-birthday"
        inputMode="numeric"
        placeholder="Preencha sua data de nascimento"
        value={form.birthday}
        onValueChange={(v) => set('birthday', v)}
        mask={maskBirthday}
      />
    </div>
  ) : null;

  const phoneField = (
    <div className="space-y-2">
      <Label htmlFor="invite-phone">
        Celular <span className="text-destructive">*</span>
      </Label>
      <MaskedInput
        id="invite-phone"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="Preencha seu celular com DDD"
        value={form.phone}
        onValueChange={(v) => set('phone', v)}
        mask={maskPhone}
      />
      {isBubblemixSlug(passbook.slug) ? (
        <p className="text-xs text-muted-foreground">
          Dê uma olhadinha no número digitado — é para ele que enviaremos o token de validação via
          WhatsApp.
        </p>
      ) : null}
    </div>
  );

  const genderField = plan.showGender ? (
    <div className="space-y-2">
      <Label>
        Gênero{childSuffix} <span className="text-destructive">*</span>
      </Label>
      <Select value={form.gender || undefined} onValueChange={(v) => set('gender', v)}>
        <SelectTrigger>
          <SelectValue placeholder="Selecione o Gênero" />
        </SelectTrigger>
        <SelectContent>
          {GENDER_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;

  const content = (
    <>
      {plan.childLabels ? <h3 className="text-sm font-semibold">Dados da Criança</h3> : null}

      {isCarPlate ? (
        <>
          {identityField}
          <div className="grid gap-4 sm:grid-cols-2">
            {customFieldsBlock}
            {nameField}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {emailField}
            {birthdayField}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {phoneField}
            {genderField}
          </div>
        </>
      ) : identityFirst ? (
        // Legado: CPF/CNPJ sempre em linha própria, antes até do nome.
        <>
          {identityField}
          <div className="grid gap-4 sm:grid-cols-2">
            {nameField}
            {emailField}
          </div>
        </>
      ) : (
        // intl-phone/hidden-generated: sem campo de documento isolado, nome vem primeiro.
        <>
          {nameField}
          {identityField}
          {emailField}
        </>
      )}

      {!isCarPlate && plan.identity !== 'intl-phone' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {birthdayField}
          {phoneField}
        </div>
      ) : !isCarPlate && plan.showBirthday ? (
        <div className="sm:w-1/2">{birthdayField}</div>
      ) : null}

      {!isCarPlate ? genderField : null}

      {plan.showStateCity ? (
        <PublicInviteStateCityFields
          idPassbook={passbook.idPassbook}
          state={form.state}
          onStateChange={(v) => set('state', v)}
          onCityChange={(v) => set('city', v)}
          city={form.city}
        />
      ) : null}

      {plan.showChildrenGroup ? (
        <PublicInviteChildrenGroup names={form.children} onChange={(c) => set('children', c)} />
      ) : null}

      {plan.childLabels && addFields.length > 0 ? (
        <h3 className="text-sm font-semibold">Dados do Responsável</h3>
      ) : null}
      {!isCarPlate ? customFieldsBlock : null}

      <PublicInviteTermsCheckbox
        checked={form.termsAccepted}
        onCheckedChange={(checked) => set('termsAccepted', checked)}
        organizationName={passbook.organizationName}
        linkTermsConditions={passbook.linkTermsConditions}
        termsAndConditions={passbook.termsAndConditions}
        accentColor={passbook.backgroundColor}
      />

      <PublicInviteMarketingConsent
        channels={passbook.marketingChannels}
        organizationName={passbook.organizationName}
        email={form.marketingEmail}
        sms={form.marketingSms}
        onEmailChange={(checked) => set('marketingEmail', checked)}
        onSmsChange={(checked) => set('marketingSms', checked)}
      />

      {/* Honeypot — invisível pra humanos, bots costumam preencher todo campo. */}
      <div className="sr-only" aria-hidden="true">
        <Label htmlFor="invite-website">Não preencha este campo</Label>
        <Input
          id="invite-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => set('website', e.target.value)}
        />
      </div>
    </>
  );

  return (
    <PublicInviteFormPanel
      onSubmit={handleFinish}
      submitting={submitting}
      submitLabel="Cadastrar"
      submitLoadingLabel="Enviando..."
      submittingOverlay={<PublicInviteSubmittingOverlay succeeded={succeeded} />}
      accentColor={passbook.backgroundColor}
      foregroundColor={passbook.foregroundColor}
    >
      {content}
    </PublicInviteFormPanel>
  );
}
